import React, { useEffect, useState } from 'react';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, BarChart, Cell,
} from 'recharts';
import Header from '../../components/header';
import Layout from '../../components/layout';
import { useDashboardApi } from '../../store/api/hooks/dashboardApiHook';
import { Dropdown, Menu } from '../../components/Dropdown';
import { downloadPDF, downloadExcel } from '../../_common/functions';

interface WRByFarmItem {
    facilityID: number;
    facilityName: string;
    userWRCount: number;
    platformAvg: number;
}

interface WROverTimeItem {
    bucketLabel: string;
    userWRCount: number;
    peerAvg: number;
    hasPeerData: boolean;
}

interface TasksByFarmItem {
    facilityID: number;
    facilityName: string;
    userTaskCount: number;
    platformAvg: number;
}

interface TasksOverTimeItem {
    bucketLabel: string;
    userTaskCount: number;
    peerAvg: number;
    hasPeerData: boolean;
}

interface ResponsivenessItem {
    binLabel: string;
    userCount: number;
    avgCount: number;
    hasPeerData: boolean;
}

interface ReactiveHoursByFarmItem {
    facilityID: number;
    facilityName: string;
    userHours: number;
    platformAvg: number;
}

interface ReactiveHoursOverTimeItem {
    bucketLabel: string;
    userHours: number;
    peerAvg: number;
    hasPeerData: boolean;
}

interface ReactiveByFarmItem {
    facilityID: number;
    facilityName: string;
    userTaskCount: number;
    platformAvg: number;
}

interface ReactiveOverTimeItem {
    bucketLabel: string;
    userTaskCount: number;
    peerAvg: number;
    hasPeerData: boolean;
}

interface PMByFarmItem {
    facilityID: number;
    facilityName: string;
    onTimeCount: number;
    lateCount: number;
    avgOnTime: number;
    avgLate: number;
}

interface PMOverTimeItem {
    bucketLabel: string;
    onTimeCount: number;
    lateCount: number;
    peerAvgOnTime: number;
    peerAvgLate: number;
    hasPeerData: boolean;
}

interface WalkthroughByFarmItem {
    facilityID: number;
    facilityName: string;
    userWalkthroughCount: number;
    platformAvg: number;
}

interface WalkthroughOverTimeItem {
    bucketLabel: string;
    userWalkthroughCount: number;
    peerAvg: number;
    hasPeerData: boolean;
}

interface WREmptyFieldItem {
    facilityID: number;
    facilityName: string;
    totalWRs: number;
    subFacilityPct: number;
    assetPct: number;
    zonePct: number;
    linePct: number;
    notePct: number;
}

interface RootCauseByFarmItem {
    facilityID: number;
    facilityName: string;
    totalResolved: number;
    withRootCause: number;
    rootCausePct: number;
}

interface RootCauseOverTimeItem {
    bucketLabel: string;
    totalCount: number;
    cockpitAlertCount: number;
    cockpitAlertPct: number;
    withoutCockpitPct: number;
}

interface WalkthroughMissingDataOverTimeItem {
    bucketLabel: string;
    totalTasks: number;
    withMissingCount: number;
    withMissingPct: number;
    withoutMissingPct: number;
}

interface WalkthroughMissingFieldItem {
    fieldType: string;
    totalFields: number;
    missingCount: number;
    missingPct: number;
}

type TimePeriod = "1W" | "3W" | "30D" | "3Mo" | "6Mo" | "12Mo";
const TIME_PERIODS: TimePeriod[] = ["1W", "3W", "30D", "3Mo", "6Mo", "12Mo"];
const periodToDays: Record<TimePeriod, number> = { "1W": 7, "3W": 21, "30D": 30, "3Mo": 90, "6Mo": 180, "12Mo": 365 };

const MyDashboard: React.FC = () => {
    const {
        getMyDashboardWRByFarm, getMyDashboardWROverTime,
        getMyDashboardTasksByFarm, getMyDashboardTasksOverTime,
        getMyDashboardResponsivenessByFarm, getMyDashboardResponsivenessIndividual,
        getMyDashboardWalkthroughsByFarm, getMyDashboardWalkthroughsOverTime,
        getMyDashboardPMByFarm, getMyDashboardPMOverTime,
        getMyDashboardReactiveByFarm, getMyDashboardReactiveOverTime,
        getMyDashboardReactiveHoursByFarm, getMyDashboardReactiveHoursOverTime,
        getMyDashboardWREmptyFields,
        getMyDashboardRootCauseFilled,
        getMyDashboardRootCauseOverTime,
        getMyDashboardWalkthroughMissingData,
        getMyDashboardWalkthroughMissingDataOverTime,
        exportMyDashboardWRByFarmToPDF, exportMyDashboardWRByFarmToXLS,
        exportMyDashboardWROverTimeToPDF, exportMyDashboardWROverTimeToXLS,
        exportMyDashboardTasksByFarmToPDF, exportMyDashboardTasksByFarmToXLS,
        exportMyDashboardTasksOverTimeToPDF, exportMyDashboardTasksOverTimeToXLS,
        exportMyDashboardRespByFarmToPDF, exportMyDashboardRespByFarmToXLS,
        exportMyDashboardRespIndToPDF, exportMyDashboardRespIndToXLS,
        exportMyDashboardWalkByFarmToPDF, exportMyDashboardWalkByFarmToXLS,
        exportMyDashboardWalkOverTimeToPDF, exportMyDashboardWalkOverTimeToXLS,
        exportMyDashboardPMByFarmToPDF, exportMyDashboardPMByFarmToXLS,
        exportMyDashboardPMOverTimeToPDF, exportMyDashboardPMOverTimeToXLS,
        exportMyDashboardReactiveByFarmToPDF, exportMyDashboardReactiveByFarmToXLS,
        exportMyDashboardReactiveOverTimeToPDF, exportMyDashboardReactiveOverTimeToXLS,
        exportMyDashboardHoursByFarmToPDF, exportMyDashboardHoursByFarmToXLS,
        exportMyDashboardHoursOverTimeToPDF, exportMyDashboardHoursOverTimeToXLS,
        exportMyDashboardRootCauseToPDF, exportMyDashboardRootCauseToXLS,
        exportMyDashboardWREmptyFieldsToPDF, exportMyDashboardWREmptyFieldsToXLS,
        exportMyDashboardWTMissingToPDF, exportMyDashboardWTMissingToXLS,
        exportMyDashboardWTMissingOverTimeToPDF, exportMyDashboardWTMissingOverTimeToXLS,
    } = useDashboardApi();

    const [timePeriod, setTimePeriod] = useState<TimePeriod>("30D");
    const days = periodToDays[timePeriod];
    const granularity = timePeriod === '1W' || timePeriod === '3W' ? 'Day' : timePeriod === '6Mo' || timePeriod === '12Mo' ? 'Month' : 'Week';
    const wrGranularity = timePeriod === '1W' ? 'Day' : timePeriod === '3W' ? 'Week' : timePeriod === '3Mo' ? 'Month' : granularity;

    const [wrByFarm, setWrByFarm] = useState<WRByFarmItem[]>([]);
    const [wrOverTime, setWrOverTime] = useState<WROverTimeItem[]>([]);
    const [wrHasPeerData, setWrHasPeerData] = useState<boolean>(true);

    const [tasksByFarm, setTasksByFarm] = useState<TasksByFarmItem[]>([]);
    const [tasksOverTime, setTasksOverTime] = useState<TasksOverTimeItem[]>([]);
    const [tasksHasPeerData, setTasksHasPeerData] = useState<boolean>(true);

    const [respByFarm, setRespByFarm] = useState<ResponsivenessItem[]>([]);
    const [respIndividual, setRespIndividual] = useState<ResponsivenessItem[]>([]);
    const [respIndHasPeerData, setRespIndHasPeerData] = useState<boolean>(true);

    const [loadingWRFarm, setLoadingWRFarm] = useState(false);
    const [loadingWRTime, setLoadingWRTime] = useState(false);
    const [loadingTaskFarm, setLoadingTaskFarm] = useState(false);
    const [loadingTaskTime, setLoadingTaskTime] = useState(false);
    const [loadingRespFarm, setLoadingRespFarm] = useState(false);
    const [loadingRespInd, setLoadingRespInd] = useState(false);

    const [walkByFarm, setWalkByFarm] = useState<WalkthroughByFarmItem[]>([]);
    const [walkOverTime, setWalkOverTime] = useState<WalkthroughOverTimeItem[]>([]);
    const [walkHasPeerData, setWalkHasPeerData] = useState<boolean>(true);
    const [loadingWalkFarm, setLoadingWalkFarm] = useState(false);
    const [loadingWalkTime, setLoadingWalkTime] = useState(false);

    const [pmByFarm, setPmByFarm] = useState<PMByFarmItem[]>([]);
    const [pmOverTime, setPmOverTime] = useState<PMOverTimeItem[]>([]);
    const [pmHasPeerData, setPmHasPeerData] = useState<boolean>(true);
    const [loadingPMFarm, setLoadingPMFarm] = useState(false);
    const [loadingPMTime, setLoadingPMTime] = useState(false);

    const [reactiveByFarm, setReactiveByFarm] = useState<ReactiveByFarmItem[]>([]);
    const [reactiveOverTime, setReactiveOverTime] = useState<ReactiveOverTimeItem[]>([]);
    const [reactiveHasPeerData, setReactiveHasPeerData] = useState<boolean>(true);
    const [loadingReactiveFarm, setLoadingReactiveFarm] = useState(false);
    const [loadingReactiveTime, setLoadingReactiveTime] = useState(false);

    const [reactiveHoursByFarm, setReactiveHoursByFarm] = useState<ReactiveHoursByFarmItem[]>([]);
    const [reactiveHoursOverTime, setReactiveHoursOverTime] = useState<ReactiveHoursOverTimeItem[]>([]);
    const [reactiveHoursHasPeerData, setReactiveHoursHasPeerData] = useState<boolean>(true);
    const [loadingReactiveHoursFarm, setLoadingReactiveHoursFarm] = useState(false);
    const [loadingReactiveHoursTime, setLoadingReactiveHoursTime] = useState(false);

    const [wrEmptyFields, setWrEmptyFields] = useState<WREmptyFieldItem[]>([]);
    const [wrEmptyTotalWRs, setWrEmptyTotalWRs] = useState<number>(0);
    const [loadingWREmpty, setLoadingWREmpty] = useState(false);

    const [rootCauseByFarm, setRootCauseByFarm] = useState<RootCauseByFarmItem[]>([]);
    const [rootCauseTotalResolved, setRootCauseTotalResolved] = useState<number>(0);
    const [rootCauseWithFilled, setRootCauseWithFilled] = useState<number>(0);
    const [rootCauseOverallPct, setRootCauseOverallPct] = useState<number>(0);
    const [loadingRootCause, setLoadingRootCause] = useState(false);
    const [rootCauseOverTime, setRootCauseOverTime] = useState<RootCauseOverTimeItem[]>([]);
    const [loadingRootCauseOverTime, setLoadingRootCauseOverTime] = useState(false);

    const [walkthroughMissingFields, setWalkthroughMissingFields] = useState<WalkthroughMissingFieldItem[]>([]);
    const [walkthroughTotal, setWalkthroughTotal] = useState<number>(0);
    const [loadingWalkthroughMissing, setLoadingWalkthroughMissing] = useState(false);
    const [walkthroughMissingOverTime, setWalkthroughMissingOverTime] = useState<WalkthroughMissingDataOverTimeItem[]>([]);
    const [loadingWalkthroughMissingOverTime, setLoadingWalkthroughMissingOverTime] = useState(false);

    const fetchWRByFarm = () => {
        setLoadingWRFarm(true);
        getMyDashboardWRByFarm(
            { days },
            (res: any) => { setWrByFarm(res?.data ?? []); setLoadingWRFarm(false); },
            () => setLoadingWRFarm(false)
        );
    };

    const fetchWROverTime = () => {
        setLoadingWRTime(true);
        getMyDashboardWROverTime(
            { days, granularity: wrGranularity },
            (res: any) => {
                setWrOverTime(res?.data ?? []);
                setWrHasPeerData(res?.hasPeerData ?? false);
                setLoadingWRTime(false);
            },
            () => setLoadingWRTime(false)
        );
    };

    const fetchTasksByFarm = () => {
        setLoadingTaskFarm(true);
        getMyDashboardTasksByFarm(
            { days },
            (res: any) => { setTasksByFarm(res?.data ?? []); setLoadingTaskFarm(false); },
            () => setLoadingTaskFarm(false)
        );
    };

    const fetchTasksOverTime = () => {
        setLoadingTaskTime(true);
        getMyDashboardTasksOverTime(
            { days, granularity: wrGranularity },
            (res: any) => {
                setTasksOverTime(res?.data ?? []);
                setTasksHasPeerData(res?.hasPeerData ?? false);
                setLoadingTaskTime(false);
            },
            () => setLoadingTaskTime(false)
        );
    };

    const fetchRespByFarm = () => {
        setLoadingRespFarm(true);
        getMyDashboardResponsivenessByFarm(
            { days },
            (res: any) => { setRespByFarm(res?.data ?? []); setLoadingRespFarm(false); },
            () => setLoadingRespFarm(false)
        );
    };

    const fetchRespIndividual = () => {
        setLoadingRespInd(true);
        getMyDashboardResponsivenessIndividual(
            { days },
            (res: any) => {
                setRespIndividual(res?.data ?? []);
                setRespIndHasPeerData(res?.hasPeerData ?? false);
                setLoadingRespInd(false);
            },
            () => setLoadingRespInd(false)
        );
    };

    const fetchWalkByFarm = () => {
        setLoadingWalkFarm(true);
        getMyDashboardWalkthroughsByFarm(
            { days },
            (res: any) => { setWalkByFarm(res?.data ?? []); setLoadingWalkFarm(false); },
            () => setLoadingWalkFarm(false)
        );
    };

    const fetchWalkOverTime = () => {
        setLoadingWalkTime(true);
        getMyDashboardWalkthroughsOverTime(
            { days, granularity: wrGranularity },
            (res: any) => {
                setWalkOverTime(res?.data ?? []);
                setWalkHasPeerData(res?.hasPeerData ?? false);
                setLoadingWalkTime(false);
            },
            () => setLoadingWalkTime(false)
        );
    };

    const fetchPMByFarm = () => {
        setLoadingPMFarm(true);
        getMyDashboardPMByFarm(
            { days },
            (res: any) => { setPmByFarm(res?.data ?? []); setLoadingPMFarm(false); },
            () => setLoadingPMFarm(false)
        );
    };

    const fetchPMOverTime = () => {
        setLoadingPMTime(true);
        getMyDashboardPMOverTime(
            { days, granularity: wrGranularity },
            (res: any) => {
                setPmOverTime(res?.data ?? []);
                setPmHasPeerData(res?.hasPeerData ?? false);
                setLoadingPMTime(false);
            },
            () => setLoadingPMTime(false)
        );
    };

    const fetchReactiveByFarm = () => {
        setLoadingReactiveFarm(true);
        getMyDashboardReactiveByFarm(
            { days },
            (res: any) => { setReactiveByFarm(res?.data ?? []); setLoadingReactiveFarm(false); },
            () => setLoadingReactiveFarm(false)
        );
    };

    const fetchReactiveOverTime = () => {
        setLoadingReactiveTime(true);
        getMyDashboardReactiveOverTime(
            { days, granularity: wrGranularity },
            (res: any) => {
                setReactiveOverTime(res?.data ?? []);
                setReactiveHasPeerData(res?.hasPeerData ?? false);
                setLoadingReactiveTime(false);
            },
            () => setLoadingReactiveTime(false)
        );
    };

    const fetchReactiveHoursByFarm = () => {
        setLoadingReactiveHoursFarm(true);
        getMyDashboardReactiveHoursByFarm(
            { days },
            (res: any) => { setReactiveHoursByFarm(res?.data ?? []); setLoadingReactiveHoursFarm(false); },
            () => setLoadingReactiveHoursFarm(false)
        );
    };

    const fetchReactiveHoursOverTime = () => {
        setLoadingReactiveHoursTime(true);
        getMyDashboardReactiveHoursOverTime(
            { days, granularity: wrGranularity },
            (res: any) => {
                setReactiveHoursOverTime(res?.data ?? []);
                setReactiveHoursHasPeerData(res?.hasPeerData ?? false);
                setLoadingReactiveHoursTime(false);
            },
            () => setLoadingReactiveHoursTime(false)
        );
    };

    const fetchRootCauseFilled = () => {
        setLoadingRootCause(true);
        getMyDashboardRootCauseFilled(
            { days },
            (res: any) => {
                setRootCauseByFarm(res?.data ?? []);
                setRootCauseTotalResolved(res?.totalResolved ?? 0);
                setRootCauseWithFilled(res?.withRootCause ?? 0);
                setRootCauseOverallPct(res?.overallPct ?? 0);
                setLoadingRootCause(false);
            },
            () => setLoadingRootCause(false)
        );
    };

    const fetchRootCauseOverTime = () => {
        setLoadingRootCauseOverTime(true);
        getMyDashboardRootCauseOverTime(
            { days, granularity: wrGranularity },
            (res: any) => { setRootCauseOverTime(res?.data ?? []); setLoadingRootCauseOverTime(false); },
            () => setLoadingRootCauseOverTime(false)
        );
    };

    const fetchWREmptyFields = () => {
        setLoadingWREmpty(true);
        getMyDashboardWREmptyFields(
            { days },
            (res: any) => {
                setWrEmptyFields(res?.data ?? []);
                setWrEmptyTotalWRs(res?.totalWRs ?? 0);
                setLoadingWREmpty(false);
            },
            () => setLoadingWREmpty(false)
        );
    };

    const fetchWalkthroughMissingData = () => {
        setLoadingWalkthroughMissing(true);
        getMyDashboardWalkthroughMissingData(
            { days },
            (res: any) => {
                setWalkthroughMissingFields(res?.data ?? []);
                setWalkthroughTotal(res?.totalWalkthroughs ?? 0);
                setLoadingWalkthroughMissing(false);
            },
            () => setLoadingWalkthroughMissing(false)
        );
    };

    const fetchWalkthroughMissingDataOverTime = () => {
        setLoadingWalkthroughMissingOverTime(true);
        getMyDashboardWalkthroughMissingDataOverTime(
            { days, granularity: wrGranularity },
            (res: any) => { setWalkthroughMissingOverTime(res?.data ?? []); setLoadingWalkthroughMissingOverTime(false); },
            () => setLoadingWalkthroughMissingOverTime(false)
        );
    };

    useEffect(() => {
        fetchWRByFarm(); fetchTasksByFarm(); fetchRespByFarm(); fetchRespIndividual();
        fetchWalkByFarm(); fetchPMByFarm(); fetchReactiveByFarm(); fetchReactiveHoursByFarm();
        fetchWREmptyFields(); fetchRootCauseFilled(); fetchRootCauseOverTime(); fetchWalkthroughMissingData(); fetchWalkthroughMissingDataOverTime();
        fetchWROverTime(); fetchTasksOverTime(); fetchWalkOverTime();
        fetchPMOverTime(); fetchReactiveOverTime(); fetchReactiveHoursOverTime();
    }, [days]);

    const CustomXTick = ({ x, y, payload }: any) => {
        const parts = String(payload.value).split('|');
        const isSunday = parts[0] === 'Sun';
        const topColor = isSunday ? '#ef4444' : '#374151';
        const subColor = isSunday ? '#ef4444' : '#6b7280';
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={14} textAnchor='middle' fontSize={11} fill={topColor} fontWeight={isSunday ? 600 : 400}>{parts[0]}</text>
                {parts[1] && <text x={0} y={0} dy={27} textAnchor='middle' fontSize={10} fill={subColor}>{parts[1]}</text>}
            </g>
        );
    };

    const LoadingState = () => (
        <div className='flex justify-center items-center h-48 text-gray-400'>Loading…</div>
    );

    const [exportingChart, setExportingChart] = useState<string | null>(null);

    const doExport = (key: string, apiFn: Function, req: any, filename: string, fmt: 'pdf' | 'xls') => {
        setExportingChart(`${key}-${fmt}`);
        apiFn(req,
            (resp: any) => { fmt === 'pdf' ? downloadPDF(resp, filename) : downloadExcel(resp, filename); setExportingChart(null); },
            () => setExportingChart(null)
        );
    };

    const ExportBtn = ({ chartKey, pdfFn, xlsFn, req, filename }: { chartKey: string; pdfFn: Function; xlsFn: Function; req: any; filename: string }) => {
        const isBusy = exportingChart?.startsWith(chartKey) ?? false;
        return (
            <Dropdown position='end'>
                <Dropdown.Button>
                    <button disabled={isBusy} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, border: '1px solid #16a34a', background: '#16a34a', cursor: isBusy ? 'not-allowed' : 'pointer', color: '#fff', opacity: isBusy ? 0.5 : 1 }}>
                        {isBusy ? '...' : 'Export'}
                    </button>
                </Dropdown.Button>
                <Dropdown.Content>
                    <Menu onClick={() => doExport(chartKey, pdfFn, req, filename, 'pdf')}>PDF</Menu>
                    <Menu onClick={() => doExport(chartKey, xlsFn, req, filename, 'xls')}>XLS</Menu>
                </Dropdown.Content>
            </Dropdown>
        );
    };

    return (
        <Layout>
            <Header title='My Dashboard'>
                <Header.Right>
                    <div className='flex gap-0.5'>
                        {TIME_PERIODS.map(p => (
                            <button
                                key={p}
                                onClick={() => setTimePeriod(p)}
                                className={`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${
                                    timePeriod === p
                                        ? 'bg-[#2B5A2E] text-white'
                                        : 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </Header.Right>
            </Header>

            <div className='flex-grow overflow-auto p-6 space-y-6'>

                {/* ── Section 1: # of WR Created ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-3'>WR Created</h2>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>

                        {/* Chart 1 — WR by Farm */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>WR Count by Farm <span className='text-[12px] text-[#6b7280] font-normal'>(line = platform avg)</span></h3>
                                <ExportBtn chartKey='wrByFarm' pdfFn={exportMyDashboardWRByFarmToPDF} xlsFn={exportMyDashboardWRByFarmToXLS} req={{ Days: days }} filename='WR Count by Farm' />
                            </div>
                            {loadingWRFarm ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={wrByFarm} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='facilityName' tick={{ fontSize: 11 }} angle={-35} textAnchor='end' interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userWRCount' name='WR Count' fill='#53BEE6' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='platformAvg' name='Platform Avg' stroke='#FF8042' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Chart 2 — WR Over Time */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    WR Count Over Time
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='wrOverTime' pdfFn={exportMyDashboardWROverTimeToPDF} xlsFn={exportMyDashboardWROverTimeToXLS} req={{ Days: days, Granularity: wrGranularity }} filename='WR Count Over Time' />
                            </div>
                            {loadingWRTime ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={wrOverTime} margin={{ top: 5, right: 20, left: 0, bottom: 55 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='bucketLabel' tick={<CustomXTick />} interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userWRCount' name='WR Count' fill='#53BEE6' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='peerAvg' name='Platform Avg' stroke='#FF8042' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Section 2: # of Tasks Completed ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-3'>Tasks Completed</h2>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>

                        {/* Chart 3 — Tasks by Farm */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Tasks Completed by Farm
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg across all farms)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='tasksByFarm' pdfFn={exportMyDashboardTasksByFarmToPDF} xlsFn={exportMyDashboardTasksByFarmToXLS} req={{ Days: days }} filename='Tasks Completed by Farm' />
                            </div>
                            {loadingTaskFarm ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={tasksByFarm} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='facilityName' tick={{ fontSize: 11 }} angle={-35} textAnchor='end' interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userTaskCount' name='Tasks Completed' fill='#6366F1' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='platformAvg' name='Platform Avg' stroke='#F59E0B' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Chart 4 — Tasks Over Time */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Tasks Completed Over Time
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='tasksOverTime' pdfFn={exportMyDashboardTasksOverTimeToPDF} xlsFn={exportMyDashboardTasksOverTimeToXLS} req={{ Days: days, Granularity: wrGranularity }} filename='Tasks Completed Over Time' />
                            </div>
                            {loadingTaskTime ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={tasksOverTime} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='bucketLabel' tick={<CustomXTick />} interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userTaskCount' name='Tasks Completed' fill='#6366F1' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='peerAvg' name='Platform Avg' stroke='#F59E0B' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Section 3: Responsiveness ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-1'>Responsiveness</h2>
                    <p className='text-[12px] text-[#6b7280] mb-4'>Time from task assignment to completion</p>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>

                        {/* Chart 5 — Responsiveness by Farm */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Avg Hours to Complete by Farm
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg hrs)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='respByFarm' pdfFn={exportMyDashboardRespByFarmToPDF} xlsFn={exportMyDashboardRespByFarmToXLS} req={{ Days: days }} filename='Responsiveness by Farm' />
                            </div>
                            {loadingRespFarm ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={respByFarm} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='binLabel' tick={{ fontSize: 11 }} angle={-35} textAnchor='end' interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} unit='h' />
                                        <Tooltip formatter={(v: any) => [`${Number(v).toFixed(1)} hrs`]} />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userCount' name='Avg Hrs' fill='#10B981' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='avgCount' name='Platform Avg' stroke='#8B5CF6' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Chart 6 — Responsiveness Individual */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    My Response Time Breakdown
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='respInd' pdfFn={exportMyDashboardRespIndToPDF} xlsFn={exportMyDashboardRespIndToXLS} req={{ Days: days }} filename='Responsiveness Individual' />
                            </div>
                            {loadingRespInd ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={respIndividual} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='binLabel' tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(v: any) => Number(v).toFixed(2)} />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userCount' name='Avg Tasks / Farm' fill='#10B981' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='avgCount' name='Platform Avg' stroke='#8B5CF6' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Section 6: Reactive Maintenance ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-1'>Reactive Maintenance</h2>
                    <p className='text-[12px] text-[#6b7280] mb-4'>Normal (non-scheduled) tasks completed</p>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>

                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    By Farm
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='reactiveByFarm' pdfFn={exportMyDashboardReactiveByFarmToPDF} xlsFn={exportMyDashboardReactiveByFarmToXLS} req={{ Days: days }} filename='Reactive by Farm' />
                            </div>
                            {loadingReactiveFarm ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={reactiveByFarm} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='facilityName' tick={{ fontSize: 11 }} angle={-35} textAnchor='end' interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userTaskCount' name='Reactive Tasks' fill='#F97316' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='platformAvg' name='Platform Avg' stroke='#64748B' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Over Time
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg)
                                    </span>
                                </h3>
                                    <ExportBtn chartKey='reactiveOverTime' pdfFn={exportMyDashboardReactiveOverTimeToPDF} xlsFn={exportMyDashboardReactiveOverTimeToXLS} req={{ Days: days, Granularity: wrGranularity }} filename='Reactive Over Time' />
                            </div>
                            {loadingReactiveTime ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={reactiveOverTime} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='bucketLabel' tick={<CustomXTick />} interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userTaskCount' name='Reactive Tasks' fill='#F97316' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='peerAvg' name='Platform Avg' stroke='#64748B' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                    </div>

                    {/* Logged Hours row */}
                    <p className='text-[13px] font-medium text-[#6b7280] mt-6 mb-3'>Logged Hours</p>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>

                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Hours by Farm
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='hoursByFarm' pdfFn={exportMyDashboardHoursByFarmToPDF} xlsFn={exportMyDashboardHoursByFarmToXLS} req={{ Days: days }} filename='Logged Hours by Farm' />
                            </div>
                            {loadingReactiveHoursFarm ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={reactiveHoursByFarm} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='facilityName' tick={{ fontSize: 11 }} angle={-35} textAnchor='end' interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} unit='h' />
                                        <Tooltip formatter={(v: any) => [`${Number(v).toFixed(1)}h`, '']} />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userHours' name='Hours Logged' fill='#FB923C' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='platformAvg' name='Platform Avg' stroke='#475569' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Hours Over Time
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg)
                                    </span>
                                </h3>
                                    <ExportBtn chartKey='hoursOverTime' pdfFn={exportMyDashboardHoursOverTimeToPDF} xlsFn={exportMyDashboardHoursOverTimeToXLS} req={{ Days: days, Granularity: wrGranularity }} filename='Logged Hours Over Time' />
                            </div>
                            {loadingReactiveHoursTime ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={reactiveHoursOverTime} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='bucketLabel' tick={<CustomXTick />} interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} unit='h' />
                                        <Tooltip formatter={(v: any) => [`${Number(v).toFixed(1)}h`, '']} />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userHours' name='Hours Logged' fill='#FB923C' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='peerAvg' name='Platform Avg' stroke='#475569' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Section 5: Preventative Maintenance ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-1'>Preventative Maintenance</h2>
                    <p className='text-[12px] text-[#6b7280] mb-4'>PM tasks completed on-time vs after scheduled date</p>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>

                        {/* Chart 9 — PM By Farm */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    By Farm
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (dashed lines = platform avg)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='pmByFarm' pdfFn={exportMyDashboardPMByFarmToPDF} xlsFn={exportMyDashboardPMByFarmToXLS} req={{ Days: days }} filename='PM by Farm' />
                            </div>
                            {loadingPMFarm ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={300}>
                                    <ComposedChart data={pmByFarm} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='facilityName' tick={{ fontSize: 11 }} angle={-35} textAnchor='end' interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='onTimeCount' name='On Time' fill='#22C55E' radius={[4, 4, 0, 0]} />
                                        <Bar dataKey='lateCount' name='Late' fill='#EF4444' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='avgOnTime' name='Avg On Time' stroke='#16A34A' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                        <Line dataKey='avgLate' name='Avg Late' stroke='#DC2626' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Chart 10 — PM Over Time */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Over Time
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (dashed = platform avg)
                                    </span>
                                </h3>
                                    <ExportBtn chartKey='pmOverTime' pdfFn={exportMyDashboardPMOverTimeToPDF} xlsFn={exportMyDashboardPMOverTimeToXLS} req={{ Days: days, Granularity: wrGranularity }} filename='PM Over Time' />
                            </div>
                            {loadingPMTime ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={300}>
                                    <ComposedChart data={pmOverTime} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='bucketLabel' tick={<CustomXTick />} interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='onTimeCount' name='On Time' fill='#22C55E' radius={[4, 4, 0, 0]} />
                                        <Bar dataKey='lateCount' name='Late' fill='#EF4444' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='peerAvgOnTime' name='Avg On Time' stroke='#16A34A' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                        <Line dataKey='peerAvgLate' name='Avg Late' stroke='#DC2626' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Section 4: Walkthroughs / Day ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-1'>Walkthroughs / Day</h2>
                    <p className='text-[12px] text-[#6b7280] mb-4'>Completions of walkthrough checklists</p>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>

                        {/* Chart 7 — Walkthroughs by Farm */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    Farm
                                    <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                        (line = platform avg across all farms)
                                    </span>
                                </h3>
                                <ExportBtn chartKey='walkByFarm' pdfFn={exportMyDashboardWalkByFarmToPDF} xlsFn={exportMyDashboardWalkByFarmToXLS} req={{ Days: days }} filename='Walkthroughs by Farm' />
                            </div>
                            {loadingWalkFarm ? <LoadingState /> : (
                                <ResponsiveContainer width='100%' height={280}>
                                    <ComposedChart data={walkByFarm} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='facilityName' tick={{ fontSize: 11 }} angle={-35} textAnchor='end' interval={0} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign='top' />
                                        <Bar dataKey='userWalkthroughCount' name='My Walkthroughs' fill='#F43F5E' radius={[4, 4, 0, 0]} />
                                        <Line dataKey='platformAvg' name='Platform Avg' stroke='#14B8A6' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Chart 8 — Walkthroughs Over Time */}
                        <div className='bg-white rounded-lg shadow-sm p-5'>
                            <div className='flex items-center justify-between mb-3'>
                                <h3 className='text-[14px] font-semibold text-[#111827]'>
                                    My Walkthroughs Over Time
                                    {walkHasPeerData && (
                                        <span className='text-[12px] text-[#6b7280] font-normal ml-2'>
                                            (line = same user type avg)
                                        </span>
                                    )}
                                </h3>
                                    <ExportBtn chartKey='walkOverTime' pdfFn={exportMyDashboardWalkOverTimeToPDF} xlsFn={exportMyDashboardWalkOverTimeToXLS} req={{ Days: days, Granularity: wrGranularity }} filename='Walkthroughs Over Time' />
                            </div>
                            {loadingWalkTime ? <LoadingState /> : (
                                <>
                                    <ResponsiveContainer width='100%' height={280}>
                                        <ComposedChart data={walkOverTime} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='bucketLabel' tick={<CustomXTick />} interval={0} />
                                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                            <Tooltip />
                                            <Legend verticalAlign='top' />
                                            <Bar dataKey='userWalkthroughCount' name='My Walkthroughs' fill='#F43F5E' radius={[4, 4, 0, 0]} />
                                            {walkHasPeerData && (
                                                <Line dataKey='peerAvg' name='Peer Avg (Same User Type)' stroke='#14B8A6' strokeDasharray='5 5' dot={false} strokeWidth={2} />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                    {!walkHasPeerData && (
                                        <p className='text-[12px] text-[#6b7280] mt-1 text-center'>
                                            No peer data available for your user type.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Section 8: Anomaly Root Cause Coverage ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-1'>Anomaly Root Cause Coverage</h2>
                    <p className='text-[12px] text-[#6b7280] mb-4'>
                        % of resolved reactive tasks (last {days} days) where Root Cause was documented
                    </p>

                    {loadingRootCause ? (
                        <LoadingState />
                    ) : (
                        <>
                            {/* Summary stat */}
                            <div className='flex gap-4 mb-6'>
                                <div className='bg-white rounded-lg shadow-sm p-5 flex-1 text-center'>
                                    <p className='text-3xl font-bold' style={{ color: rootCauseOverallPct >= 80 ? '#22C55E' : rootCauseOverallPct >= 50 ? '#F59E0B' : '#EF4444' }}>
                                        {Number(rootCauseOverallPct).toFixed(1)}%
                                    </p>
                                    <p className='text-[12px] text-[#6b7280] mt-1'>Root Cause Filled</p>
                                    <p className='text-[12px] text-[#6b7280] mt-1'>{rootCauseWithFilled} of {rootCauseTotalResolved} resolved anomalies</p>
                                </div>
                                <div className='bg-white rounded-lg shadow-sm p-5 flex-1 text-center'>
                                    <p className='text-3xl font-bold text-gray-700'>{rootCauseTotalResolved - rootCauseWithFilled}</p>
                                    <p className='text-[12px] text-[#6b7280] mt-1'>Missing Root Cause</p>
                                    <p className='text-[12px] text-[#6b7280] mt-1'>of {rootCauseTotalResolved} resolved anomalies</p>
                                </div>
                            </div>

                            {/* Over Time — stacked % */}
                            <div className='bg-white rounded-lg shadow-sm p-5'>
                                <div className='flex items-center justify-between mb-3'>
                                    <h3 className='text-[14px] font-semibold text-[#111827]'>Over Time</h3>
                                    <ExportBtn chartKey='rootCause' pdfFn={exportMyDashboardRootCauseToPDF} xlsFn={exportMyDashboardRootCauseToXLS} req={{ Days: days }} filename='Root Cause Coverage' />
                                </div>
                                {loadingRootCauseOverTime ? (
                                    <LoadingState />
                                ) : (
                                    <>
                                        <ResponsiveContainer width='100%' height={260}>
                                            <BarChart data={rootCauseOverTime} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                                <CartesianGrid strokeDasharray='3 3' vertical={false} />
                                                <XAxis
                                                    dataKey='bucketLabel'
                                                    tick={<CustomXTick />}
                                                    interval={0}
                                                    height={44}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11 }}
                                                    domain={[0, 100]}
                                                    unit='%'
                                                />
                                                <Tooltip
                                                    formatter={(value: any, name: any, props: any) => {
                                                        const d = props.payload;
                                                        if (name === 'With Cockpit Alert')
                                                            return [`${Number(value).toFixed(1)}%  (${d.cockpitAlertCount} of ${d.totalCount})`, name];
                                                        return [`${Number(value).toFixed(1)}%`, name];
                                                    }}
                                                />
                                                <Legend verticalAlign='top' height={28} iconType='square' />
                                                <Bar dataKey='cockpitAlertPct'   name='With Cockpit Alert'    stackId='a' fill='#22C55E' />
                                                <Bar dataKey='withoutCockpitPct' name='Without Cockpit Alert' stackId='a' fill='#FCA5A5' radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* ── Section 9: WR Data Completeness ── */}

                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-1'>WR Data Completeness</h2>
                    <p className='text-[12px] text-[#6b7280] mb-4'>
                        % of your Work Requests (last {days} days) with each field left empty
                        {wrEmptyTotalWRs > 0 && (
                            <span className='ml-2 text-gray-500 font-medium'>— {wrEmptyTotalWRs} WR{wrEmptyTotalWRs !== 1 ? 's' : ''} total</span>
                        )}
                    </p>

                    <div className='bg-white rounded-lg shadow-sm p-5'>
                        <div className='flex items-center justify-between mb-3'>
                            <h3 className='text-[14px] font-semibold text-[#111827]'>Fields with Missing Data</h3>
                            <ExportBtn chartKey='wrEmpty' pdfFn={exportMyDashboardWREmptyFieldsToPDF} xlsFn={exportMyDashboardWREmptyFieldsToXLS} req={{ Days: days }} filename='WR Data Completeness' />
                        </div>
                        {loadingWREmpty ? (
                            <LoadingState />
                        ) : (() => {
                            const fields: { key: keyof WREmptyFieldItem; label: string }[] = [
                                { key: 'subFacilityPct', label: 'Sub-facility' },
                                { key: 'assetPct',       label: 'Asset' },
                                { key: 'zonePct',        label: 'Zone' },
                                { key: 'linePct',        label: 'Line' },
                                { key: 'notePct',        label: 'Note' },
                            ];
                            const pctBadge = (v: number) => {
                                const color = v >= 50 ? '#EF4444' : v >= 20 ? '#F59E0B' : '#22C55E';
                                const bg    = v >= 50 ? '#FEE2E2' : v >= 20 ? '#FEF3C7' : '#DCFCE7';
                                return { color, bg };
                            };
                            const totalWRs = wrEmptyFields.reduce((s, r) => s + r.totalWRs, 0);
                            const weightedPct = (key: keyof WREmptyFieldItem) => totalWRs === 0 ? 0 :
                                wrEmptyFields.reduce((s, r) => s + (r[key] as number) * r.totalWRs, 0) / totalWRs;
                            const thCls = 'text-center px-4 py-3 text-[13px] font-medium text-[#374151] border-b border-[#e5e7eb]';
                            const tdCls = 'text-center px-4 py-4 text-[13px] text-[#374151] border-b border-[#e5e7eb]';
                            return (
                                <div className='overflow-x-auto'>
                                    <table className='w-full text-[13px] border-collapse bg-white'>
                                        <thead>
                                            <tr className='border-b border-[#e5e7eb]'>
                                                <th className='text-left px-4 py-3 text-[13px] font-medium text-[#374151] border-b border-[#e5e7eb]'>Farm</th>
                                                <th className={thCls}>Total WR</th>
                                                {fields.map(f => <th key={f.key} className={thCls}>{f.label}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {wrEmptyFields.map(row => (
                                                <tr key={row.facilityID} className='hover:bg-[#f9fafb]'>
                                                    <td className='text-left px-4 py-4 text-[13px] text-[#374151] border-b border-[#e5e7eb]'>{row.facilityName}</td>
                                                    <td className={tdCls}>{row.totalWRs}</td>
                                                    {fields.map(f => {
                                                        const v = row[f.key] as number;
                                                        const { color, bg } = pctBadge(v);
                                                        return (
                                                            <td key={f.key} className={tdCls}>
                                                                <span style={{ color, background: bg, padding: '2px 10px', borderRadius: '12px', fontWeight: 500, fontSize: '12px' }}>
                                                                    {v.toFixed(2)}%
                                                                </span>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className='border-t-2 border-[#e5e7eb]'>
                                                <td className='text-left px-4 py-4 text-[13px] font-bold text-[#111827] border-t border-[#e5e7eb]'>Total</td>
                                                <td className='text-center px-4 py-4 text-[13px] font-bold text-[#111827] border-t border-[#e5e7eb]'>{totalWRs}</td>
                                                {fields.map(f => {
                                                    const v = weightedPct(f.key);
                                                    const { color, bg } = pctBadge(v);
                                                    return (
                                                        <td key={f.key} className='text-center px-4 py-4 border-t border-[#e5e7eb]'>
                                                            <span style={{ color, background: bg, padding: '2px 10px', borderRadius: '12px', fontWeight: 700, fontSize: '12px' }}>
                                                                {v.toFixed(2)}%
                                                            </span>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        </tfoot>
                                    </table>
                                    <div className='flex gap-5 mt-3 text-[12px] text-[#6b7280] justify-center'>
                                        <span className='flex items-center gap-1'><span className='inline-block w-3 h-3 rounded-full bg-green-500' /> &lt;20% missing</span>
                                        <span className='flex items-center gap-1'><span className='inline-block w-3 h-3 rounded-full bg-amber-400' /> 20–50% missing</span>
                                        <span className='flex items-center gap-1'><span className='inline-block w-3 h-3 rounded-full bg-red-500' /> &gt;50% missing</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* ── Section 10: Walkthrough Data Quality ── */}
                <div>
                    <h2 className='text-[14px] font-semibold text-[#374151] mb-1'>Walkthrough Data Quality</h2>
                    <p className='text-[12px] text-[#6b7280] mb-4'>
                        % of walkthroughs with missing checklist data (last {days} days)
                    </p>

                    {/* Over Time stacked % */}
                    <div className='bg-white rounded-lg shadow-sm p-5'>
                        <div className='flex items-center justify-between mb-3'>
                            <h3 className='text-[14px] font-semibold text-[#111827]'>Over Time</h3>
                            <ExportBtn chartKey='wtMissingOT' pdfFn={exportMyDashboardWTMissingOverTimeToPDF} xlsFn={exportMyDashboardWTMissingOverTimeToXLS} req={{ Days: days }} filename='Walkthrough Missing Data' />
                        </div>
                        {loadingWalkthroughMissingOverTime ? (
                            <LoadingState />
                        ) : (
                            <>
                                <ResponsiveContainer width='100%' height={260}>
                                    <BarChart data={walkthroughMissingOverTime} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                        <CartesianGrid strokeDasharray='3 3' vertical={false} />
                                        <XAxis
                                            dataKey='bucketLabel'
                                            tick={<CustomXTick />}
                                            interval={0}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            domain={[0, 100]}
                                            tickFormatter={(v: any) => `${v}%`}
                                            tick={{ fontSize: 11 }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Tooltip
                                            formatter={(value: any, name: any, props: any) => {
                                                const d = props.payload;
                                                if (name === 'With Missing Data')
                                                    return [`${Number(value).toFixed(1)}%  (${d.withMissingCount} of ${d.totalTasks})`, name];
                                                return [`${Number(value).toFixed(1)}%`, name];
                                            }}
                                        />
                                        <Legend verticalAlign='top' height={28} iconType='square' />
                                        <Bar dataKey='withMissingPct'    name='With Missing Data'    stackId='a' fill='#EF4444' />
                                        <Bar dataKey='withoutMissingPct' name='Without Missing Data' stackId='a' fill='#22C55E' radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </>
                        )}
                    </div>
                </div>

            </div>
        </Layout>
    );
};

export default MyDashboard;
